import type { ReactNode } from 'react'
import { detalhesDoErro, mensagemDoErro, requestIdDoErro } from '../api/erros'
import { cn } from '../lib/cn'
import { TEXTOS } from '../lib/glossario'
import { IconeAlerta } from './Icones'

/**
 * Tela vazia com orientação.
 *
 * Tabela em branco é ambígua: o usuário não sabe se o filtro não achou nada, se ainda
 * não existe registro, ou se o sistema falhou. Cada um desses casos tem um texto e uma
 * saída diferentes, e é isso que este componente obriga a declarar.
 *
 * @param props título, orientação, um ícone opcional e a ação sugerida
 * @returns o bloco de estado vazio
 */
export function EstadoVazio({
  titulo,
  ajuda,
  acao,
  icone,
}: {
  titulo: string
  ajuda: string
  acao?: ReactNode
  icone?: ReactNode
}) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
      {icone && <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center text-slate-400">{icone}</div>}
      <h3 className="text-base font-semibold text-slate-900">{titulo}</h3>
      <p className="mx-auto mt-2 max-w-prose text-sm text-slate-600">{ajuda}</p>
      {acao && <div className="mt-5 flex justify-center">{acao}</div>}
    </div>
  )
}

/**
 * Erro com o que o servidor explicou, não com "erro inesperado".
 *
 * As mensagens do backend já são em português e dizem o que fazer; o `requestId` vai
 * junto porque é o que permite o suporte achar a requisição no log sem pedir print.
 *
 * @param props o erro capturado e um título opcional
 * @returns o bloco de erro
 */
export function AvisoDeErro({ erro, titulo }: { erro: unknown; titulo?: string }) {
  const detalhes = detalhesDoErro(erro)
  const requestId = requestIdDoErro(erro)

  return (
    <div role="alert" className="flex gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900">
      <IconeAlerta className="mt-0.5 h-5 w-5 text-red-600" />
      <div className="min-w-0">
        {titulo && <p className="font-semibold">{titulo}</p>}
        <p className={titulo ? 'mt-1' : undefined}>{mensagemDoErro(erro)}</p>
        {detalhes.length > 0 && (
          <ul className="mt-2 list-inside list-disc space-y-1">
            {detalhes.map((detalhe) => (
              <li key={detalhe}>{detalhe}</li>
            ))}
          </ul>
        )}
        {requestId && <p className="mt-2 text-xs text-red-700">Ao pedir ajuda, informe o código {requestId}.</p>}
      </div>
    </div>
  )
}

/** Espera anunciada a leitor de tela — `aria-busy` sozinho não é lido em região estática. */
export function Carregando({ texto = TEXTOS.carregando }: { texto?: string }) {
  return (
    <p role="status" aria-live="polite" className="flex items-center justify-center gap-2 py-8 text-sm text-slate-500">
      <span
        aria-hidden="true"
        className="h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-niteroi-600"
      />
      {texto}
    </p>
  )
}

/** Larguras diferentes por linha: barras do mesmo tamanho parecem tabela, não texto carregando. */
const LARGURAS = ['w-full', 'w-11/12', 'w-8/12']

/**
 * Espaço reservado enquanto a lista ou o cartão carregam.
 *
 * Ocupa a altura que o conteúdo vai ocupar. Sem isso, a página monta curta, o conteúdo
 * chega e tudo salta para baixo — quem já tinha ido clicar em "Nova solicitação" clica em
 * outra coisa. O texto continua existindo para o leitor de tela: barra cinza não se anuncia.
 *
 * @param props quantas linhas simular e classes de layout
 * @returns o esqueleto
 */
export function Esqueleto({ linhas = 3, className }: { linhas?: number; className?: string }) {
  return (
    <div role="status" aria-live="polite" className={cn('space-y-3', className)}>
      <span className="sr-only">{TEXTOS.carregando}</span>
      {Array.from({ length: linhas }, (_, indice) => (
        <div
          key={indice}
          aria-hidden="true"
          className={cn('h-4 animate-pulse rounded bg-slate-200', LARGURAS[indice % LARGURAS.length])}
        />
      ))}
    </div>
  )
}
